export type ClipboardMode = 'copy' | 'cut';

export interface FileClipboardPayload {
  mode: ClipboardMode;
  paths: string[];
  ts: number;
}

const STORAGE_KEY = 'argon_fb_clipboard';

type Listener = (payload: FileClipboardPayload | null) => void;

const listeners = new Set<Listener>();

function readRaw(): FileClipboardPayload | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as FileClipboardPayload;
    if (!parsed || !Array.isArray(parsed.paths) || (parsed.mode !== 'copy' && parsed.mode !== 'cut')) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function notify(payload: FileClipboardPayload | null) {
  listeners.forEach((fn) => {
    try {
      fn(payload);
    } catch {
      /* ignore listener errors */
    }
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (ev) => {
    if (ev.key === STORAGE_KEY) {
      notify(readRaw());
    }
  });
}

export const fileClipboardService = {
  get(): FileClipboardPayload | null {
    return readRaw();
  },

  set(mode: ClipboardMode, paths: string[]) {
    const unique = Array.from(new Set(paths.filter(Boolean)));
    const payload: FileClipboardPayload = {
      mode,
      paths: unique,
      ts: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    notify(payload);
    return payload;
  },

  clear() {
    localStorage.removeItem(STORAGE_KEY);
    notify(null);
  },

  /** Same-tab + cross-window updates. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    listener(readRaw());
    return () => {
      listeners.delete(listener);
    };
  },
};

export default fileClipboardService;
